<template>
  <section class="flex flex-col gap-4 rounded-lg border p-4 bg-white">
    <p v-if="!hasValidChecklistTemplateableContext" class="text-sm text-destructive">
      Invalid checklist templateable context.
    </p>

    <header class="flex items-center justify-between gap-3">
      <h3 class="text-base font-semibold text-foreground">
        <Badge variant="outline">
          {{ visibleChecklistTemplates.length }}
        </Badge> Checklist Templates
      </h3>
      <div class="flex items-center gap-2">
        <Button type="button" size="sm" variant="outline" :disabled="!hasValidChecklistTemplateableContext" @click="openSelectModal">
          Attach Existing
        </Button>
        <Button type="button" size="sm" :disabled="!hasValidChecklistTemplateableContext" @click="openCreateModal">
          Create Checklist Template
        </Button>
      </div>
    </header>




    <p v-if="createErrorMessage" class="text-sm text-destructive">
      {{ createErrorMessage }}
    </p>

    <p v-if="loadErrorMessage" class="text-sm text-destructive">
      {{ loadErrorMessage }}
    </p>

    <div v-if="checklistTemplatesStore.isLoading && visibleChecklistTemplates.length === 0" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
      Loading checklist templates...
    </div>

    <p v-else-if="!checklistTemplatesStore.isLoading && visibleChecklistTemplates.length === 0" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
      No checklist templates are linked to this record yet.
    </p>

    <UmisisAnimatedList v-else :items="visibleChecklistTemplates" list-class="flex flex-col gap-4">
      <template #default="{ item: checklistTemplate }">
        <ChecklistTemplateEditor
          v-if="editingChecklistTemplateFor(checklistTemplate.id)"
          :checklist-template="editingChecklistTemplateFor(checklistTemplate.id)"
          @updated="handleChecklistTemplateEditorUpdated(checklistTemplate.id, $event)"
          @published="handleChecklistTemplateEditorPublished(checklistTemplate.id, $event)"
          @aborted="handleChecklistTemplateEditorAborted(checklistTemplate.id)"
        />
        <ChecklistTemplate
          v-else
          :checklist-template="checklistTemplate"
          show-manage-actions
          :initially-minimized="true"
          :is-deleting="deletingChecklistTemplateIds.includes(checklistTemplate.id)"
          :is-duplicating="duplicatingChecklistTemplateIds.includes(checklistTemplate.id)"
          :is-processing="startingEditChecklistTemplateIds.includes(checklistTemplate.id)"
          :delete-action-label="destructiveActionLabel"
          :deleting-action-label="destructiveActionLoadingLabel"
          @edit="openChecklistTemplateEditor"
          @duplicate="duplicateChecklistTemplate"
          @delete="removeChecklistTemplate"
        />
      </template>
    </UmisisAnimatedList>

    <ChecklistTemplateCreateModal
      ref="createChecklistTemplateModal"
      :checklist-templateable="checklistTemplateable"
      :edit-route="editRoute"
      :redirect-to-edit-page-after-create="redirectToEditPageAfterCreate"
      @created="handleChecklistTemplateCreated"
    />

    <ChecklistTemplateSelectModal
      ref="selectChecklistTemplateModal"
      :checklist-templateable="checklistTemplateable"
      :attached-checklist-template-ids="attachedChecklistTemplateIds"
      @attached="handleChecklistTemplateAttached"
    />
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import UmisisAnimatedList from '../../../../../../../resources/js/components/umisis/generic/UmisisAnimatedList.vue'
import { Button } from '@/components/ui/button/index.js'
import ChecklistTemplate from './ChecklistTemplate.vue'
import ChecklistTemplateEditor from './ChecklistTemplateEditor.vue'
import ChecklistTemplateCreateModal from './ChecklistTemplateCreateModal.vue'
import ChecklistTemplateSelectModal from './ChecklistTemplateSelectModal.vue'
import { useChecklistTemplatesStore } from '../../stores/checklistTemplatesStore.js'
import { Badge } from '../../../../../../../resources/js/components/ui/badge/index.js'

const props = defineProps({
  checklistTemplateableId: {
    type: [Number, String],
    default: null,
  },
  checklistTemplateableType: {
    type: String,
    default: '',
  },
  editRoute: {
    type: String,
    default: 'ChecklistTemplateEditPage',
  },
  redirectToEditPageAfterCreate: {
    type: Boolean,
    default: true,
  },
})

const initialChecklistTemplateableId = props.checklistTemplateableId
const initialChecklistTemplateableType = props.checklistTemplateableType
const checklistTemplatesStore = useChecklistTemplatesStore(`checklist_templates_card_${String(initialChecklistTemplateableType).replace(/[^A-Za-z0-9]/g, '_')}_${initialChecklistTemplateableId}`)
const createChecklistTemplateModal = ref(null)
const selectChecklistTemplateModal = ref(null)
const createErrorMessage = ref(null)
const deletingChecklistTemplateIds = ref([])
const duplicatingChecklistTemplateIds = ref([])
const expandedChecklistTemplateIds = ref([])
const editingChecklistTemplates = ref({})
const startingEditChecklistTemplateIds = ref([])

const editRoute = computed(() => props.editRoute)
const redirectToEditPageAfterCreate = computed(() => props.redirectToEditPageAfterCreate)
const checklistTemplateableId = computed(() => {
  return Number(props.checklistTemplateableId ?? 0)
})
const checklistTemplateableType = computed(() => {
  return String(props.checklistTemplateableType || '').trim()
})
const hasValidChecklistTemplateableContext = computed(() => {
  return Number.isInteger(checklistTemplateableId.value)
    && checklistTemplateableId.value > 0
    && checklistTemplateableType.value.length > 0
})
const shouldDetachChecklistTemplate = computed(() => hasValidChecklistTemplateableContext.value)
const checklistTemplateable = computed(() => {
  return {
    id: checklistTemplateableId.value,
    type: checklistTemplateableType.value,
  }
})
const destructiveActionLabel = computed(() => {
  return shouldDetachChecklistTemplate.value ? 'Detach' : 'Delete'
})
const destructiveActionLoadingLabel = computed(() => {
  return shouldDetachChecklistTemplate.value ? 'Detaching...' : 'Deleting...'
})
const attachedChecklistTemplateIds = computed(() => {
  return checklistTemplatesStore.items
    .map((checklistTemplate) => Number(checklistTemplate.id))
    .filter((checklistTemplateId) => checklistTemplateId > 0)
})

const resolveChecklistTemplateSortTimestamp = (checklistTemplate) => {
  const createdAt = Date.parse(String(checklistTemplate?.created_at ?? ''))

  if (!Number.isNaN(createdAt)) {
    return createdAt
  }

  return 0
}

const visibleChecklistTemplates = computed(() => {
  return [...checklistTemplatesStore.items]
    .filter((checklistTemplate) => {
      if (checklistTemplate.status === 'archived') {
        return false
      }

      const isDraftVersion = checklistTemplate.status === 'draft'
        && Number(checklistTemplate.draft_of_id ?? 0) > 0

      return !isDraftVersion
    })
    .sort((leftChecklistTemplate, rightChecklistTemplate) => {
      const timestampDifference = resolveChecklistTemplateSortTimestamp(rightChecklistTemplate)
        - resolveChecklistTemplateSortTimestamp(leftChecklistTemplate)

      if (timestampDifference !== 0) {
        return timestampDifference
      }

      return Number(rightChecklistTemplate.id ?? 0) - Number(leftChecklistTemplate.id ?? 0)
    })
})

const loadErrorMessage = computed(() => {
  if (!checklistTemplatesStore.error) {
    return null
  }

  return checklistTemplatesStore.error.response?.data?.message ?? checklistTemplatesStore.error.message ?? 'Unable to load checklist templates.'
})

const openCreateModal = () => {
  if (!hasValidChecklistTemplateableContext.value) {
    return
  }

  createChecklistTemplateModal.value?.openModal()
}

const openSelectModal = () => {
  if (!hasValidChecklistTemplateableContext.value) {
    return
  }

  selectChecklistTemplateModal.value?.openModal()
}

const loadChecklistTemplates = async () => {
  if (!hasValidChecklistTemplateableContext.value) {
    checklistTemplatesStore.items = []
    return
  }

  await checklistTemplatesStore.fetchItems({
    clear: true,
    not_paginated: true,
    checklist_templateable_id: checklistTemplateableId.value,
    checklist_templateable_type: checklistTemplateableType.value,
    order_by: 'created_at',
    order_direction: 'desc',
  })
}

const isChecklistTemplateExpanded = (checklistTemplateId) => {
  return expandedChecklistTemplateIds.value.includes(Number(checklistTemplateId))
}

const handleChecklistTemplateCreated = (checklistTemplate) => {
  if (!checklistTemplate || Number(checklistTemplate.id) < 1) {
    return
  }

  checklistTemplatesStore.itemPatchState(checklistTemplate)

  if (redirectToEditPageAfterCreate.value) {
    return
  }

  setEditingChecklistTemplate(checklistTemplate.id, checklistTemplate)
}

const handleChecklistTemplateAttached = (checklistTemplate) => {
  if (!checklistTemplate || Number(checklistTemplate.id) < 1) {
    return
  }

  checklistTemplatesStore.itemPatchState(checklistTemplate)
}

const duplicateChecklistTemplate = async (checklistTemplate) => {
  const checklistTemplateId = Number(checklistTemplate?.id ?? 0)

  if (checklistTemplateId < 1) {
    return
  }

  duplicatingChecklistTemplateIds.value.push(checklistTemplateId)

  try {
    await checklistTemplatesStore.duplicateChecklistTemplate(checklistTemplateId, {
      checklist_templateable_id: checklistTemplateableId.value,
      checklist_templateable_type: checklistTemplateableType.value,
    })
    await loadChecklistTemplates()
    createErrorMessage.value = null
  } catch (error) {
    createErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to duplicate checklist template.'
  } finally {
    duplicatingChecklistTemplateIds.value = duplicatingChecklistTemplateIds.value.filter((id) => id !== checklistTemplateId)
  }
}

const editingChecklistTemplateFor = (checklistTemplateId) => {
  return editingChecklistTemplates.value[Number(checklistTemplateId)] ?? null
}

const setEditingChecklistTemplate = (checklistTemplateId, checklistTemplate) => {
  editingChecklistTemplates.value = {
    ...editingChecklistTemplates.value,
    [Number(checklistTemplateId)]: checklistTemplate,
  }
}

const clearEditingChecklistTemplate = (checklistTemplateId) => {
  const normalizedChecklistTemplateId = Number(checklistTemplateId)

  if (!Object.prototype.hasOwnProperty.call(editingChecklistTemplates.value, normalizedChecklistTemplateId)) {
    return
  }

  const nextEditingChecklistTemplates = { ...editingChecklistTemplates.value }
  delete nextEditingChecklistTemplates[normalizedChecklistTemplateId]
  editingChecklistTemplates.value = nextEditingChecklistTemplates
}

const openChecklistTemplateEditor = async (checklistTemplate) => {
  const checklistTemplateId = Number(checklistTemplate?.id ?? 0)

  if (checklistTemplateId < 1) {
    return
  }

  startingEditChecklistTemplateIds.value.push(checklistTemplateId)

  try {
    const draftChecklistTemplate = await checklistTemplatesStore.beginChecklistTemplateEditing(checklistTemplateId)

    if (!draftChecklistTemplate) {
      return
    }

    setEditingChecklistTemplate(checklistTemplateId, draftChecklistTemplate)
    createErrorMessage.value = null
  } catch (error) {
    createErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to edit checklist template.'
  } finally {
    startingEditChecklistTemplateIds.value = startingEditChecklistTemplateIds.value.filter((id) => id !== checklistTemplateId)
  }
}

const handleChecklistTemplateEditorUpdated = (checklistTemplateId, updatedChecklistTemplate) => {
  if (!updatedChecklistTemplate) {
    return
  }

  setEditingChecklistTemplate(checklistTemplateId, updatedChecklistTemplate)
  checklistTemplatesStore.itemPatchState(updatedChecklistTemplate)
}

const handleChecklistTemplateEditorPublished = async (checklistTemplateId, publishedChecklistTemplate) => {
  clearEditingChecklistTemplate(checklistTemplateId)

  if (publishedChecklistTemplate) {
    checklistTemplatesStore.itemPatchState(publishedChecklistTemplate)
  }

  await loadChecklistTemplates()
}

const handleChecklistTemplateEditorAborted = async (checklistTemplateId) => {
  clearEditingChecklistTemplate(checklistTemplateId)
  await loadChecklistTemplates()
}

const removeChecklistTemplate = async (checklistTemplate) => {
  const confirmationMessage = shouldDetachChecklistTemplate.value
    ? 'Detach this checklist template from this record?'
    : 'Delete this checklist template?'

  if (!window.confirm(confirmationMessage)) {
    return
  }

  deletingChecklistTemplateIds.value.push(checklistTemplate.id)

  try {
    if (shouldDetachChecklistTemplate.value) {
      await checklistTemplatesStore.detachChecklistTemplate(checklistTemplate.id, {
        checklist_templateable_id: checklistTemplateableId.value,
        checklist_templateable_type: checklistTemplateableType.value,
      })
      clearEditingChecklistTemplate(checklistTemplate.id)
    } else {
      await checklistTemplatesStore.itemDelete(checklistTemplate.id)
    }

    createErrorMessage.value = null
  } catch (error) {
    createErrorMessage.value = error.response?.data?.message
      ?? error.message
      ?? (shouldDetachChecklistTemplate.value
        ? 'Unable to detach checklist template.'
        : 'Unable to delete checklist template.')
  } finally {
    deletingChecklistTemplateIds.value = deletingChecklistTemplateIds.value.filter((id) => id !== checklistTemplate.id)
  }
}

watch(() => [checklistTemplateableId.value, checklistTemplateableType.value], async () => {
  editingChecklistTemplates.value = {}
  startingEditChecklistTemplateIds.value = []
  expandedChecklistTemplateIds.value = []
  await loadChecklistTemplates()
})

onMounted(async () => {
  await loadChecklistTemplates()
})
</script>
